import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtMoney, fmtDate } from "../lib/api";
import { PageHeader, KpiCard, DataTable, StatusBadge } from "../components/Shell";
import { Button } from "../components/ui/button";
import MonthlyBusinessRecapDialog from "../components/MonthlyBusinessRecapDialog";
import BusinessSplitWidget from "../components/BusinessSplitWidget";
import BusinessTrendWidget from "../components/BusinessTrendWidget";
import {
  ReceiptX,
  Wallet,
  UsersThree,
  ShieldCheck,
  ArrowRight,
  Warning,
  Siren,
  ClockCounterClockwise,
  CurrencyEur,
  HourglassMedium,
  CheckCircle,
  ChartLine,
  ChartLineUp,
} from "@phosphor-icons/react";

export default function PatronDashboard() {
  const [stats, setStats] = useState(null);
  const [requests, setRequests] = useState([]);
  const [payments, setPayments] = useState([]);
  const [timesheets, setTimesheets] = useState([]);
  const [staff, setStaff] = useState([]);
  const [recapOpen, setRecapOpen] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    Promise.all([
      api.get("/stats/dashboard"),
      api.get("/payment-requests"),
      api.get("/payments"),
      api.get("/timesheets"),
      api.get("/staff"),
    ]).then(([s, r, p, t, st]) => {
      setStats(s.data);
      setRequests(r.data);
      setPayments(p.data);
      setTimesheets(t.data);
      setStaff(st.data);
    });
  }, []);

  const derived = useMemo(() => {
    if (!stats) return null;
    const aPayer = requests
      .filter((r) => r.statut === "a_payer")
      .reduce((s, r) => s + (r.montant_calcule || 0), 0);
    const pendingCount = requests.filter((r) => r.statut === "demande_envoyee").length;
    const aPayerCount = requests.filter((r) => r.statut === "a_payer").length;
    const enRetardCount = requests.filter((r) => r.en_retard).length;
    const incomplets = timesheets.filter((t) => t.incomplet);

    // Current month timesheets (interne)
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
    const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0, 10);
    const internesIds = staff.filter((s) => s.type_personnel === "interne").map((s) => s.id);
    const secuIds = staff.filter((s) => s.type_personnel === "securite").map((s) => s.id);
    const monthHours = timesheets
      .filter((t) => internesIds.includes(t.personnel_id) && t.date_jour >= monthStart && t.date_jour <= monthEnd)
      .reduce((s, t) => s + (t.duree_heures || 0), 0);

    // Current week (monday-sunday) timesheets (securite)
    const day = now.getDay() || 7;
    const monday = new Date(now);
    monday.setDate(now.getDate() - day + 1);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    const weekStart = monday.toISOString().slice(0, 10);
    const weekEnd = sunday.toISOString().slice(0, 10);
    const secuHours = timesheets
      .filter((t) => secuIds.includes(t.personnel_id) && t.date_jour >= weekStart && t.date_jour <= weekEnd)
      .reduce((s, t) => s + (t.duree_heures || 0), 0);

    return {
      aPayer,
      aPayerCount,
      pendingCount,
      enRetardCount,
      incompletsCount: incomplets.length,
      monthHours: Math.round(monthHours * 100) / 100,
      secuHours: Math.round(secuHours * 100) / 100,
      monthLabel: `${monthStart} → ${monthEnd}`,
      weekLabel: `${weekStart} → ${weekEnd}`,
    };
  }, [stats, requests, payments, timesheets, staff]);

  if (!stats || !derived) return <div className="text-[#a1a1aa]">Chargement...</div>;

  const demandesATraiter = requests
    .filter((r) => ["demande_envoyee", "a_payer"].includes(r.statut))
    .sort((a, b) => (b.en_retard ? 1 : 0) - (a.en_retard ? 1 : 0))
    .slice(0, 5);
  const derniersReglements = payments.slice(0, 5);
  const urgencesCount = derived.aPayerCount + derived.enRetardCount + derived.incompletsCount;

  return (
    <div className="space-y-8">
      <PageHeader
        testId="patron-dashboard-header"
        title="Espace Patron"
        subtitle="Tableau de pilotage — paiements, danseuses, personnel."
      />

      {/* 1. Pilotage rapide — 4 KPIs prioritaires */}
      <Section title="Pilotage rapide" icon={ChartLine}>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <BigKpi
            testId="bk-a-payer-now"
            icon={CurrencyEur}
            label="À payer maintenant"
            value={fmtMoney(derived.aPayer)}
            tone="primary"
            sub={`${derived.aPayerCount} demande${derived.aPayerCount > 1 ? "s" : ""}`}
            onClick={() => nav("/payment-requests")}
          />
          <BigKpi
            testId="bk-en-attente"
            icon={HourglassMedium}
            label="Demandes en attente"
            value={derived.pendingCount}
            tone="neutral"
            sub={derived.pendingCount > 0 ? "À valider par la manageuse" : "Rien à valider"}
            onClick={() => nav("/payment-requests")}
          />
          <BigKpi
            testId="bk-paye"
            icon={CheckCircle}
            label="Total payé"
            value={fmtMoney(stats.total_paye)}
            tone="success"
            sub={`${payments.length} règlement${payments.length > 1 ? "s" : ""}`}
            onClick={() => nav("/payments")}
          />
          <BigKpi
            testId="bk-reste"
            icon={Wallet}
            label="Reste à payer"
            value={fmtMoney(stats.reste_a_payer)}
            tone={stats.reste_a_payer > 0 ? "danger" : "neutral"}
            sub="Solde global danseuses"
          />
        </div>
      </Section>

      {/* 2. Actions rapides */}
      <Section title="Actions rapides">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <QuickActionBtn testId="qa-demandes" icon={ReceiptX} label="Demandes à payer" to="/payment-requests" primary />
          <QuickActionBtn testId="qa-reglements" icon={Wallet} label="Règlements" to="/payments" />
          <QuickActionBtn testId="qa-danseuses" icon={UsersThree} label="Danseuses" to="/dancers" />
          <QuickActionBtn testId="qa-personnel" icon={ShieldCheck} label="Personnel" to="/staff" />
          <button
            data-testid="qa-recap-mois"
            onClick={() => setRecapOpen(true)}
            className="group flex flex-col items-center justify-center gap-2 rounded-md border border-[#27272a] bg-[#121217] hover:bg-[#1a1a21] hover:border-[#fbbf24] transition-all p-4 text-sm"
          >
            <ChartLineUp size={22} color="#fbbf24" weight="fill" />
            <span className="text-white group-hover:text-[#fbbf24] transition-colors">Récap fin de mois</span>
          </button>
        </div>
      </Section>

      {/* 2b. Répartition CA Danseuses / Boissons */}
      <Section title="Répartition du chiffre d'affaires" icon={ChartLineUp}>
        <div className="space-y-4">
          <BusinessSplitWidget />
          <BusinessTrendWidget months={6} />
        </div>
      </Section>

      {/* 3. Urgences */}
      {urgencesCount > 0 && (
        <Section title={`Urgences / à traiter (${urgencesCount})`} icon={Siren} iconColor="#f87171">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {derived.aPayerCount > 0 && (
              <UrgenceCard
                testId="urgence-a-payer"
                icon={CurrencyEur}
                tone="#fbbf24"
                count={derived.aPayerCount}
                label="Demandes validées à payer"
                hint={fmtMoney(derived.aPayer)}
                onClick={() => nav("/payment-requests")}
              />
            )}
            {derived.enRetardCount > 0 && (
              <UrgenceCard
                testId="urgence-retard"
                icon={Warning}
                tone="#ef4444"
                count={derived.enRetardCount}
                label="Règlements en retard"
                hint="Validés depuis plus de 48 h"
                onClick={() => nav("/payment-requests")}
              />
            )}
            {derived.incompletsCount > 0 && (
              <UrgenceCard
                testId="urgence-pointages"
                icon={ClockCounterClockwise}
                tone="#f87171"
                count={derived.incompletsCount}
                label="Pointages incomplets"
                hint="À vérifier avec la manageuse"
                onClick={() => nav("/timesheets")}
              />
            )}
          </div>
        </Section>
      )}

      {/* Section A - Paiements */}
      <Section title="Paiements" icon={Wallet}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="label-xs">Demandes à traiter</div>
              <Button
                data-testid="view-all-requests"
                size="sm"
                variant="outline"
                onClick={() => nav("/payment-requests")}
                className="h-8 border-[#27272a] text-white hover:bg-[#1a1a21]"
              >
                Voir toutes les demandes <ArrowRight size={14} className="ml-1" />
              </Button>
            </div>
            <DataTable
              columns={[
                { title: "Scène", render: (r) => <b>{r.nom_scene}</b> },
                { title: "Facture", key: "numero_facture" },
                { title: "Montant", align: "right", render: (r) => fmtMoney(r.montant_calcule) },
                {
                  title: "Statut",
                  render: (r) => (
                    <div className="flex items-center gap-1.5">
                      <StatusBadge status={r.statut} />
                      {r.en_retard && <StatusBadge status="en_retard" />}
                    </div>
                  ),
                },
              ]}
              rows={demandesATraiter}
              rowKey="id"
              emptyText="Aucune demande à traiter"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="label-xs">Derniers règlements</div>
              <Button
                data-testid="view-all-payments"
                size="sm"
                variant="outline"
                onClick={() => nav("/payments")}
                className="h-8 border-[#27272a] text-white hover:bg-[#1a1a21]"
              >
                Voir tous les règlements <ArrowRight size={14} className="ml-1" />
              </Button>
            </div>
            <DataTable
              columns={[
                { title: "Date", render: (p) => fmtDate(p.date_reglement) },
                { title: "Scène", key: "nom_scene" },
                { title: "Montant", align: "right", render: (p) => fmtMoney(p.montant) },
                { title: "Mode", key: "mode_reglement" },
              ]}
              rows={derniersReglements}
              rowKey="id"
              emptyText="Aucun règlement effectué"
            />
          </div>
        </div>
      </Section>

      {/* Section B - Danseuses */}
      <Section title="Danseuses" icon={UsersThree}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <KpiCard testId="pat-bookings-actifs" label="Bookings actifs" value={stats.bookings_actifs} />
          <KpiCard testId="pat-gain-moyen" label="Gain moyen / jour" value={fmtMoney(stats.gain_moyen_jour)} />
          <KpiCard testId="pat-meilleure-soiree" label="Meilleure soirée" value={fmtMoney(stats.meilleure_soiree)} tone="success" />
          <KpiCard testId="pat-total-du" label="Total dû" value={fmtMoney(stats.total_net)} tone="jaunes" />
        </div>
        <div className="flex items-center justify-between mb-3">
          <div className="label-xs">Top 5 danseuses par gains</div>
          <Button
            data-testid="view-dancers"
            size="sm"
            variant="outline"
            onClick={() => nav("/dancers")}
            className="h-8 border-[#27272a] text-white hover:bg-[#1a1a21]"
          >
            Voir les danseuses <ArrowRight size={14} className="ml-1" />
          </Button>
        </div>
        <DataTable
          columns={[
            { title: "Scène", render: (r) => <b>{r.nom_scene}</b> },
            { title: "Gains", align: "right", render: (r) => fmtMoney(r.gains) },
            { title: "Billets", align: "right", render: (r) => <span style={{ color: "#34d399" }}>{r.billets}</span> },
            { title: "Jaunes", align: "right", render: (r) => <span style={{ color: "#fbbf24" }}>{r.jaunes}</span> },
            { title: "Jours", align: "right", key: "jours" },
          ]}
          rows={stats.top_gains || []}
          emptyText="Pas de données sur cette période"
        />
      </Section>

      {/* Section C - Personnel */}
      <Section title="Personnel" icon={ShieldCheck}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <KpiCard
            testId="pat-pointages-incomplets"
            label="Pointages incomplets"
            value={derived.incompletsCount}
            tone={derived.incompletsCount ? "danger" : "default"}
          />
          <KpiCard testId="pat-mois-heures" label="Heures du mois" value={`${derived.monthHours} h`} hint="Personnel interne" />
          <KpiCard testId="pat-secu-heures" label="Sécurité (semaine)" value={`${derived.secuHours} h`} hint="Semaine en cours" />
          <KpiCard testId="pat-personnel-actif" label="Personnel actif" value={stats.personnel_actif} />
        </div>
        <Button
          data-testid="view-staff"
          variant="outline"
          onClick={() => nav("/staff")}
          className="border-[#27272a] text-white hover:bg-[#1a1a21]"
        >
          Voir le personnel <ArrowRight size={14} className="ml-2" />
        </Button>
      </Section>

      {/* Section D - Statistiques détaillées (repliée) */}
      <details className="surface p-5">
        <summary className="label-xs cursor-pointer hover:text-white select-none flex items-center gap-2">
          <ChartLine size={16} />
          Statistiques détaillées (billets, jetons, brut, logement, acomptes)
        </summary>
        <div className="pt-5 space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiCard label="Billets" value={stats.total_billets} tone="billets" hint="1€ × 45%" />
            <KpiCard label="Jetons jaunes" value={stats.total_jaunes} tone="jaunes" hint="20€ × 45%" />
            <KpiCard label="Jetons rouges" value={stats.total_rouges} tone="rouges" hint="10€" />
            <KpiCard label="Jetons bleus" value={stats.total_bleus} tone="bleus" hint="5€" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <KpiCard label="Total brut généré" value={fmtMoney(stats.total_brut)} />
            <KpiCard label="Logement déduit" value={fmtMoney(stats.total_logement)} />
            <KpiCard label="Acomptes" value={fmtMoney(stats.total_acomptes)} />
          </div>
        </div>
      </details>

      <MonthlyBusinessRecapDialog open={recapOpen} onOpenChange={setRecapOpen} />
    </div>
  );
}

function Section({ title, icon: Icon, iconColor, children }) {
  return (
    <section>
      <div className="flex items-center gap-2 mb-4">
        {Icon && <Icon size={18} color={iconColor || "#fbbf24"} weight={iconColor ? "fill" : "regular"} />}
        <h2 className="font-display text-xl md:text-2xl font-medium tracking-tight">{title}</h2>
      </div>
      {children}
    </section>
  );
}

function BigKpi({ testId, icon: Icon, label, value, sub, tone = "neutral", onClick }) {
  const toneMap = {
    primary: { bg: "#fbbf24", text: "#09090b", border: "transparent" },
    success: { bg: "#121217", text: "#34d399", border: "#27272a" },
    danger: { bg: "#121217", text: "#f87171", border: "#27272a" },
    neutral: { bg: "#121217", text: "#ffffff", border: "#27272a" },
  };
  const t = toneMap[tone] || toneMap.neutral;
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      disabled={!onClick}
      className="group rounded-md border p-4 md:p-5 text-left transition-all duration-200 hover:scale-[1.01]"
      style={{ background: t.bg, borderColor: t.border, cursor: onClick ? "pointer" : "default" }}
    >
      <div className="flex items-start justify-between">
        <div className="label-xs" style={{ color: tone === "primary" ? "rgba(9,9,11,0.7)" : "#71717a" }}>
          {label}
        </div>
        {Icon && <Icon size={20} weight="fill" color={t.text} />}
      </div>
      <div className="font-display text-3xl md:text-4xl font-light tracking-tight mt-3" style={{ color: t.text }}>
        {value}
      </div>
      {sub && (
        <div className="text-xs mt-1.5" style={{ color: tone === "primary" ? "rgba(9,9,11,0.7)" : "#71717a" }}>
          {sub}
        </div>
      )}
    </button>
  );
}

function QuickActionBtn({ testId, icon: Icon, label, to, primary }) {
  const nav = useNavigate();
  return (
    <button
      data-testid={testId}
      onClick={() => nav(to)}
      className={`flex flex-col items-center justify-center gap-2 rounded-md border px-3 py-5 md:py-6 transition-all duration-200 ${
        primary
          ? "bg-[#fbbf24] hover:bg-[#f59e0b] border-transparent text-black"
          : "bg-[#121217] hover:bg-[#1a1a21] border-[#27272a] text-white hover:border-[#52525b]"
      }`}
    >
      <Icon size={26} weight={primary ? "fill" : "regular"} />
      <span className="text-sm md:text-base font-medium text-center leading-tight">{label}</span>
    </button>
  );
}

function UrgenceCard({ testId, icon: Icon, tone, count, label, hint, onClick }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className="surface p-4 text-left hover:bg-[#1a1a21] transition-all duration-200 flex items-start gap-3"
      style={{ borderLeft: `3px solid ${tone}` }}
    >
      <Icon size={22} color={tone} weight="fill" className="mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2">
          <span className="font-display text-2xl font-light" style={{ color: tone }}>{count}</span>
          <span className="text-sm font-medium">{label}</span>
        </div>
        {hint && <div className="text-xs text-[#71717a] mt-1 truncate">{hint}</div>}
      </div>
      <ArrowRight size={16} color="#71717a" className="mt-1 shrink-0" />
    </button>
  );
}
